import type { UseMutationResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { FunctionComponent, ReactNode } from 'react';
import type { ItemSettingsDto, PanelSettingsDto } from '../../api/generated/client';
import type { CashOccurrenceType } from '../../enums';
import type { ISetting } from '../../interfaces/simulation-settings';
import { SettingBoxBase } from './SettingBoxBase';
import { SettingBoxCashflowTable } from './SettingBoxCashflowTable';

interface IProps {
    title: string;
    cashOccurrenceType: CashOccurrenceType;
    settings?: PanelSettingsDto;
    cashflowItems?: ItemSettingsDto[];
    updatePanelSettings: UseMutationResult<ISetting, AxiosError, ISetting>;
    updateItemSettings: UseMutationResult<ISetting, AxiosError, ISetting>;
    detailHeader?: ReactNode;
    showToDateColumn?: boolean;
    showButton?: boolean;
    disableButton?: boolean;
    onButtonClick?: () => void;
    sectionKey?: string;
    expandedKey?: string;
    isUpdating?: boolean;
    hideDetailHeaderWhenEmpty?: boolean;
    isLiquiditySurplus?: boolean;
}

export const CashflowSettingBox: FunctionComponent<IProps> = ({
    title,
    cashOccurrenceType,
    settings,
    cashflowItems,
    updatePanelSettings,
    updateItemSettings,
    detailHeader,
    showToDateColumn,
    showButton,
    disableButton,
    onButtonClick,
    sectionKey,
    expandedKey,
    isUpdating,
    hideDetailHeaderWhenEmpty,
    isLiquiditySurplus,
}) => {
    const items = cashflowItems ?? settings?.itemsSettings ?? [];
    const isEmpty = items.filter((item) => !item.hidden).length === 0;

    return (
        <SettingBoxBase
            title={title}
            boxId={settings?.id}
            toggled={settings?.enabled}
            onToggle={updatePanelSettings}
            showButton={showButton}
            disableButton={disableButton}
            onButtonClick={onButtonClick}
            sectionKey={sectionKey}
            expandedKey={expandedKey}
            detail={
                <>
                    {(!hideDetailHeaderWhenEmpty || !isEmpty) && detailHeader}
                    <SettingBoxCashflowTable
                        enabled={!!settings?.enabled}
                        cashOccurrenceType={cashOccurrenceType}
                        updateItemSettings={updateItemSettings}
                        cashflowItems={items}
                        showToDateColumn={showToDateColumn}
                        showEmptyState={true}
                        isUpdating={isUpdating}
                        isLiquiditySurplus={isLiquiditySurplus}
                    />
                </>
            }
        />
    );
};
